import { useAppDispatch, useAppSelector } from 'app/hooks';
import { AxiosError } from 'axios';
import AlertModal from 'components/common/AlertModal';
import ConfirmModal from 'components/common/ConfirmModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import SortingGuide from 'components/common/SortingGuide';
import menuBookEditorService from 'features/MenuBook/menuBookEditorService';
import {
  menuBookEditorActions,
  selectMenuBookEditorData,
  selectMenuBookEditorDeletedSubGenreMenuIds,
  selectMenuBookEditorSelectedItemIds,
} from 'features/MenuBook/menuBookEditorSlice';
import {
  Genre,
  Menu,
  MenuBookEditorDeletePayload,
  MenuBookEditorGetAllPayload,
  SubGenre,
  SubMenuGroup,
} from 'features/MenuBook/models';
import { mutliDragAwareReorder } from 'features/MenuBook/utils';
import { selectIsEditMode } from 'features/common/editModeSlice';
import masterDataService from 'features/common/masterDataService';
import { CouponClassificationMap } from 'features/common/models';
import _ from 'lodash';
import moment from 'moment';
import { FormEvent, Fragment, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { DragDropContext, DragStart, DropResult } from 'react-beautiful-dnd';
import { Accordion, Button, Col, Form, Modal, Row } from 'react-bootstrap';
import ReactDatePicker from 'react-datepicker';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FaCalendarAlt } from 'react-icons/fa';
import { DATE_FORMAT_YYYY_MM_DD, MENU_TYPE, displayError, handleServiceError } from 'utils';
import CopyModal from '../CopyModal';
import GenreAddModal, { GenreAddModalReturnData } from '../GenreAddModal';
import GenreRowList from '../GenreRowList';
import MenuAddModal, { MenuAddModalReturnData } from '../MenuAddModal';
import SubGenreAddModal, { SubGenreAddModalReturnData } from '../SubGenreAddModal';
import './style.scss';

const SEARCH_SHOW_DELETE_KEY_DEFAULT = '1';

interface GenreAddModalData {
  menuBookId?: number;
  courseId?: number;
  exceptGenreIds: number[];
}

interface SubGenreAddModalData {
  genreId: number;
  genreName: string;
  exceptSubgenreIds: number[];
}

interface MenuAddModalData {
  genreId: number;
  subGenreId: number;
  genreName: string;
  subgenreName: string;
  exceptMenuIds: number[];
  dataDate: Date;
}

interface CopyModalData {
  menuBookId: number | string;
  menuBookName: string | null;
  courseId: number | string;
  courseName: string | null;
  genreId: number | string;
  genreName: string | null;
  subGenreId: number | string;
  subGenreName: string | null;
  menuId: number | string;
  menuName: string | null;
}

export interface FormSearchValueProps {
  showFlg: '0' | '1' | '2';
  dataDate?: Date;
}

export interface MenuBookEditorModalProps {
  menuBookId?: number;
  menuBookName?: string;
  courseId?: number;
  courseName?: string;
  onClose?: () => void;
}

export const getListStyle = (isDraggingOver: boolean) => ({
  background: isDraggingOver ? '#999' : 'lightgrey',
});

export const getItemStyle = (
  isDragging: boolean,
  draggableStyle: any,
  isSelected?: boolean,
  isGhosting?: boolean,
  backgroundColorDefault: string = '#fff'
) => {
  let backgroundColor = backgroundColorDefault;

  if (isGhosting) {
    backgroundColor = '#FAFBFC';
  }

  if (isSelected) {
    backgroundColor = '#DEEBFF';
  }

  if (isDragging) {
    backgroundColor = '#ffff8d';
  }

  return {
    userSelect: 'none',
    background: backgroundColor,
    opacity: isGhosting ? '0' : '1',
    ...draggableStyle,
  };
};

export const COLUMN_WIDTH = [4, 14, 14, 12, 10, 12, 6, 10, 12, 6];
const CustomInputDatePicker = forwardRef((props: any, ref: any) => {
  // You can style this further
  return (
    <>
      <div style={{ position: 'relative' }}>
        <FaCalendarAlt style={{ position: 'absolute', top: '8px', left: '10px' }} onClick={props.onClick} />
        <input
          className="form-control text-center"
          onClick={props.onClick}
          style={{ paddingLeft: '32px' }}
          value={props.value}
          onChange={props.onChange}
          type="text"
        />
      </div>
    </>
  );
});

function MenuBookEditorModal({ menuBookId, menuBookName, courseId, courseName, onClose }: MenuBookEditorModalProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { showGlobalModal } = useGlobalModal();

  //ref
  const rootData = useRef<Genre[]>([]);
  const btnAddGenreRef = useRef<HTMLButtonElement>(null);
  const btnAddSubgenreMenuRef = useRef<HTMLButtonElement>(null);
  const btnCopyRef = useRef<HTMLButtonElement>(null);
  const btnDeleteRef = useRef<HTMLButtonElement>(null);
  const formSearchRef = useRef<HTMLFormElement>(null);
  const [formSearchValue, setFormSearchValue] = useState<FormSearchValueProps>({
    showFlg: '1',
    dataDate: new Date(),
  });

  //selector
  const data = useAppSelector(selectMenuBookEditorData);
  const deletedSubGenreMenuIds = useAppSelector(selectMenuBookEditorDeletedSubGenreMenuIds);
  const selectedItemIds = useAppSelector(selectMenuBookEditorSelectedItemIds);
  const isEditMode = useAppSelector(selectIsEditMode);

  //source
  const SHOW_STATUS_SOURCE: { [key: string]: string } = {
    '0': t('MenuBookListPage.show_all'),
    '1': t('MenuBookListPage.show_valid'),
    '2': t('MenuBookListPage.show_deleted'),
  };

  // state of GenreAddModal
  const [showGenreAddModal, setShowGenreAddModal] = useState(false);
  const [genreAddModalData, setGenreAddModalData] = useState<GenreAddModalData>();

  // state of SubGenreAddModal
  const [showSubGenreAddModal, setShowSubGenreAddModal] = useState(false);
  const [subgenreAddModalData, setSubgenreAddModalData] = useState<SubGenreAddModalData>();

  // state of MenuAddModal
  const [showMenuAddModal, setShowMenuAddModal] = useState(false);
  const [menuAddModalData, setMenuAddModalData] = useState<MenuAddModalData>();

  // state of CopyModal
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [copyModalData, setCopyModalData] = useState<CopyModalData>();

  function handleClose() {
    // check form change data
    const isFormChanged = checkFormDataChanged();
    if (isFormChanged) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('MenuBookListPage.warning_save_data')}
          onConfirm={handleSave.bind(null, true)}
          onCancel={onClose}
        />
      ));
    } else {
      onClose?.();
    }
  }

  function handleExpandAll() {
    dispatch(menuBookEditorActions.setExpandAll(true));
  }

  function handleCollapseAll() {
    dispatch(menuBookEditorActions.setExpandAll(false));
  }

  async function loadMenuBookEditorData() {
    if (formSearchRef.current) {
      const showFlg = formSearchRef.current['show_flg'].value.trim() as string;
      const dataDate = formSearchValue.dataDate;
      const payload: MenuBookEditorGetAllPayload = {
        show_flg: showFlg,
        data_date: dataDate || new Date(),
        menu_book_id: menuBookId,
        course_id: courseId,
      };

      try {
        const response = await menuBookEditorService.getAll(payload);
        rootData.current = response.data;
        dispatch(menuBookEditorActions.fetchDataSuccess(response.data));
      } catch (error) {
        if (!handleServiceError(showGlobalModal, error as AxiosError)) {
          displayError(showGlobalModal, error as AxiosError);
        }
      }
    }
  }

  async function loadCouponClassificationSource() {
    //load data coupon_classification
    try {
      const response = await masterDataService.getCouponClassification();
      const couponClassificationMap = response.data.coupon_classification.reduce(
        (couponClassificationMap: CouponClassificationMap, coupon) => {
          couponClassificationMap[coupon.id] = coupon.name;
          return couponClassificationMap;
        },
        {}
      );
      dispatch(menuBookEditorActions.setCouponClassificationSource(couponClassificationMap));
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }
  }

  // handle status button actions
  useEffect(() => {
    if (btnAddGenreRef.current) btnAddGenreRef.current.disabled = false;
    if (btnAddSubgenreMenuRef.current) btnAddSubgenreMenuRef.current.disabled = true;
    if (btnCopyRef.current) btnCopyRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }, []);

  useEffect(() => {
    //load editor data
    (async () => {
      await loadCouponClassificationSource();
      await loadMenuBookEditorData();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectedRowChanged = useCallback(
    function () {
      if (btnAddGenreRef.current && btnAddSubgenreMenuRef.current && btnCopyRef.current && btnDeleteRef.current) {
        const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-mbeditor-item > input:checked');
        if (chkSelectionElems.length === 0) {
          btnAddGenreRef.current.disabled = false;
          btnAddSubgenreMenuRef.current.disabled = true;
          btnCopyRef.current.disabled = true;
          btnDeleteRef.current.disabled = true;
        } else if (chkSelectionElems.length === 1) {
          const chkSelectionElem = chkSelectionElems[0];
          if (chkSelectionElem.dataset.type === 'genre') {
            btnAddGenreRef.current.disabled = true;
            btnAddSubgenreMenuRef.current.disabled = false;
            btnCopyRef.current.disabled = false;
            btnDeleteRef.current.disabled = false;
          } else if (chkSelectionElem.dataset.type === 'subgenre') {
            btnAddGenreRef.current.disabled = true;
            btnAddSubgenreMenuRef.current.disabled = false;
            btnCopyRef.current.disabled = false;
            btnDeleteRef.current.disabled = false;
          } else if (chkSelectionElem.dataset.type === 'menu') {
            btnAddGenreRef.current.disabled = true;
            btnAddSubgenreMenuRef.current.disabled = true;
            btnCopyRef.current.disabled = false;
            btnDeleteRef.current.disabled = false;
          }
        } else {
          btnAddGenreRef.current.disabled = true;
          btnAddSubgenreMenuRef.current.disabled = true;
          btnCopyRef.current.disabled = true;
          btnDeleteRef.current.disabled = false;
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [btnAddGenreRef.current, btnAddSubgenreMenuRef.current, btnCopyRef.current, btnDeleteRef.current]
  );

  const onDragStart = useCallback(
    function (start: DragStart) {
      const id: number = Number(start.draggableId.split('-').splice(-1).pop());
      const selected = selectedItemIds.find((itemId: number): boolean => itemId === id);

      // if dragging an item that is not selected - unselect all items
      if (!selected) {
        dispatch(menuBookEditorActions.unselectAll());
      }
      dispatch(menuBookEditorActions.setDraggingItemId(id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  const onDragEnd = useCallback(
    function (result: DropResult) {
      const destination = result.destination;
      const source = result.source;

      // nothing to do
      if (!destination || result.reason === 'CANCEL') {
        dispatch(menuBookEditorActions.setDraggingItemId(undefined));
        return;
      }

      const type = result.type.split('-')[0];
      const droppableId = result.source.droppableId;

      const reorderData = mutliDragAwareReorder({
        data,
        selectedItemIds,
        source,
        destination,
        type,
        droppableId,
      });

      dispatch(
        menuBookEditorActions.updateState({
          ...reorderData,
          selectedDroppableId: undefined,
          draggingItemId: undefined,
        })
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    handleUnSelectAll();

    // check form change data
    const isFormChanged = checkFormDataChanged();
    if (isFormChanged) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('MenuBookListPage.warning_save_data')}
          onConfirm={handleSave}
        />
      ));
    } else {
      //load editor data
      loadMenuBookEditorData();
    }
  }

  useEffect(() => {
    dispatch(menuBookEditorActions.setFormSearchValue(formSearchValue));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formSearchValue]);

  function handleCollapsedFormSearch() {
    if (formSearchRef.current) {
      const newFormSearchValue = {
        ...formSearchValue,
        showFlg: formSearchRef.current['show_flg'].value.trim(),
      };
      setFormSearchValue(newFormSearchValue);
    }
  }

  function processDelete() {
    const chkSelectionElems = document.querySelectorAll<HTMLInputElement>('.chk-mbeditor-item > input:checked');
    if (chkSelectionElems.length > 0) {
      const deletePayload: MenuBookEditorDeletePayload = {
        menubookGenres: [],
        courseGenres: [],
        subgenres: [],
        menus: [],
      };
      chkSelectionElems.forEach((chkSelectionElem) => {
        if (chkSelectionElem.dataset.deletetype === 'menubook_genres') {
          deletePayload.menubookGenres.push({
            genreId: Number(chkSelectionElem.value),
          });
        } else if (chkSelectionElem.dataset.deletetype === 'course_genres') {
          deletePayload.courseGenres.push({
            genreId: Number(chkSelectionElem.value),
          });
        } else if (chkSelectionElem.dataset.deletetype === 'subgenres') {
          deletePayload.subgenres.push({
            genreId: Number(chkSelectionElem.dataset.genreid),
            subGenreId: Number(chkSelectionElem.value),
          });
        } else if (chkSelectionElem.dataset.deletetype === 'menus') {
          deletePayload.menus.push({
            genreId: Number(chkSelectionElem.dataset.genreid),
            subGenreId: Number(chkSelectionElem.dataset.subgenreid),
            menuId: Number(chkSelectionElem.value),
            subGenreMenuId: Number(chkSelectionElem.dataset.subgenre_menu_id),
          });
        }
      });

      dispatch(menuBookEditorActions.delete(deletePayload));
      handleUnSelectAll();
    }
  }

  function handleDelete() {
    const chkSelectionElems = document.querySelectorAll('.chk-mbeditor-item > input:checked');
    if (chkSelectionElems.length > 0) {
      showGlobalModal(() => (
        <ConfirmModal
          title={t('App.confirm')}
          message={t('MenuBookListPage.warn_confirm_delete')}
          onConfirm={processDelete}
        />
      ));
    }
  }

  function handleSave(isSaveAndClose?: boolean) {
    (async () => {
      try {
        const response = await menuBookEditorService.save({
          menu_book_id: menuBookId,
          course_id: courseId,
          genres: data,
          deleted_subgenre_menu_ids: deletedSubGenreMenuIds,
          data_date: formSearchValue.dataDate || new Date(),
        });
        rootData.current = data;
        dispatch(menuBookEditorActions.resetDeletedSubGenreMenuIds());
        loadMenuBookEditorData();
        showGlobalModal(() => (
          <AlertModal
            title={t('App.info')}
            message={response.message}
            onClose={() => {
              if (isSaveAndClose) {
                onClose?.();
              }
            }}
          />
        ));
      } catch (e) {
        if (!handleServiceError(showGlobalModal, e as AxiosError)) {
          displayError(showGlobalModal, e as AxiosError);
        }
      }
    })();
  }

  function handleOpenGenreAddModal() {
    // compose exceptGenreIds
    const exceptGenreIds = data.map((genre) => genre.id);
    setGenreAddModalData({
      menuBookId: menuBookId,
      courseId: courseId,
      exceptGenreIds: exceptGenreIds,
    });

    setShowGenreAddModal(true);
  }

  function handleCloseGenreAddModal() {
    handleUnSelectAll();
    setShowGenreAddModal(false);
    setGenreAddModalData(undefined);
  }

  function handleReflectGenreAddModal(returnData: GenreAddModalReturnData) {
    const newGenres: Genre[] = [];
    returnData.selectedGenres.forEach((selectedGenre) => {
      const newGenre: Genre = {
        id: selectedGenre.id,
        name: selectedGenre.name,
        menu_book_id: Number(menuBookId),
        course_id: courseId || null,
        subgenres: [],
      };
      newGenres.push(newGenre);
    });

    if (newGenres.length > 0) {
      dispatch(menuBookEditorActions.addGenres(newGenres));
    }

    handleCloseGenreAddModal();
  }

  // add subgenre, menu modal
  function handleOpenAddSubgenreOrAddMenuModal() {
    const elements = document.querySelectorAll<HTMLInputElement>('.chk-mbeditor-item > input:checked');

    if (elements.length === 1) {
      const selectedElement = elements[0];
      if (selectedElement.dataset.type === 'genre') {
        const genreId = Number(selectedElement.value);
        // const menuSettingGenreId = Number(selectedElement.dataset.menu_setting_genre_id);
        const genreName = selectedElement.dataset.genre_name || '';
        const subgenreIds = selectedElement.dataset.subgenre_ids?.split(',').map((item) => Number(item)) || [];

        const subGenreAddModalData: SubGenreAddModalData = {
          genreId,
          genreName,
          exceptSubgenreIds: subgenreIds,
        };
        handleOpenSubGenreAddModal(subGenreAddModalData);
      } else if (selectedElement.dataset.type === 'subgenre') {
        const subGenreId = Number(selectedElement.value);
        const genreId = Number(selectedElement.dataset.genreid);
        // const genreSubgenreId = Number(selectedElement.dataset.genre_subgenre_id);
        const genreName = selectedElement.dataset.genre_name || '';
        const subgenreName = selectedElement.dataset.subgenre_name || '';
        const menuIds = selectedElement.dataset.menu_ids?.split(',').map((item) => Number(item)) || [];

        const menuAddModalData: MenuAddModalData = {
          genreId,
          subGenreId,
          // genreSubgenreId,
          genreName,
          subgenreName,
          exceptMenuIds: menuIds,
          dataDate: formSearchValue?.dataDate || new Date(),
        };
        handleOpenMenuAddModal(menuAddModalData);
      }
    }
  }

  // handle SubGenreAddModal Events
  function handleOpenSubGenreAddModal(subGenreAddModalData: SubGenreAddModalData) {
    setSubgenreAddModalData(subGenreAddModalData);
    setShowSubGenreAddModal(true);
  }

  function handleCloseSubGenreAddModal() {
    handleUnSelectAll();
    setShowSubGenreAddModal(false);
    setSubgenreAddModalData(undefined);
  }

  function handleReflectSubGenreAddModal(returnData: SubGenreAddModalReturnData) {
    const newSubGenres: SubGenre[] = [];
    returnData.selectedSubGenres.forEach((selectedSubGenre) => {
      const newSubGenre: SubGenre = {
        id: selectedSubGenre.id,
        name: selectedSubGenre.name,
        genre_id: returnData.genreId as number,
        menus: [],
      };
      newSubGenres.push(newSubGenre);
    });

    if (newSubGenres.length > 0) {
      dispatch(menuBookEditorActions.addSubGenres({ genreId: returnData.genreId as number, subGenres: newSubGenres }));
    }

    handleCloseSubGenreAddModal();
  }

  // handle MenuAddModal Events
  function handleOpenMenuAddModal(menuAddModalData: MenuAddModalData) {
    setMenuAddModalData(menuAddModalData);
    setShowMenuAddModal(true);
  }

  function handleCloseMenuAddModal() {
    handleUnSelectAll();
    setShowMenuAddModal(false);
    setMenuAddModalData(undefined);
  }

  async function handleReflectMenuAddModal(returnData: MenuAddModalReturnData) {
    const menuIds = returnData.selectedMenus.map((item) => item.id);
    try {
      const targetDate = formSearchValue?.dataDate || new Date();
      const response = await menuBookEditorService.getMenuData(menuIds, targetDate);
      const menuDatas = response.data;

      const newMenus: Menu[] = [];
      returnData.selectedMenus.forEach((selectedMenu) => {
        let subMenuGroups: SubMenuGroup[] = [];
        const menuData = menuDatas.find((item) => item.menu_id === selectedMenu.id);
        if (menuData) {
          subMenuGroups = menuData.sub_menu_groups;
        }

        const newMenu: Menu = {
          id: selectedMenu.id,
          name: selectedMenu.name,
          subgenre_id: returnData.subGenreId as number,
          coupon_classification: selectedMenu.coupon_classification,
          deleted: false,
          sub_menu_groups: subMenuGroups,
        };
        newMenus.push(newMenu);
      });

      if (newMenus.length > 0) {
        dispatch(
          menuBookEditorActions.addMenus({
            genreId: returnData.genreId as number,
            subGenreId: returnData.subGenreId as number,
            menus: newMenus,
          })
        );
      }
    } catch (e) {
      if (!handleServiceError(showGlobalModal, e as AxiosError)) {
        displayError(showGlobalModal, e as AxiosError);
      }
    }

    handleCloseMenuAddModal();
  }

  function handleUnSelectAll() {
    const chkSelectionElems = document.querySelectorAll('.chk-mbeditor-item > input:checked');
    chkSelectionElems.forEach(function (elem) {
      (elem as HTMLInputElement).checked = false;
    });

    if (btnAddGenreRef.current) btnAddGenreRef.current.disabled = false;
    if (btnAddSubgenreMenuRef.current) btnAddSubgenreMenuRef.current.disabled = true;
    if (btnCopyRef.current) btnCopyRef.current.disabled = true;
    if (btnDeleteRef.current) btnDeleteRef.current.disabled = true;
  }

  //handle CopyModal event
  function handleOpenCopyModal() {
    const elements = document.querySelectorAll<HTMLInputElement>('.chk-mbeditor-item > input:checked');

    if (elements.length === 1) {
      // check form change data
      const isFormChanged = checkFormDataChanged();
      if (isFormChanged) {
        showGlobalModal(() => (
          <ConfirmModal
            title={t('App.confirm')}
            message={t('MenuBookListPage.warning_save_data')}
            onConfirm={handleSave.bind(null, false)}
          />
        ));
        return;
      }

      const selectedElement = elements[0];
      if (selectedElement.dataset.type === 'genre') {
        const genreId = Number(selectedElement.value);
        const genreName = selectedElement.dataset.genre_name || '';

        // compose copyModalData
        const copyModalData: CopyModalData = {
          menuBookId: menuBookId || '',
          menuBookName: menuBookName || '',
          courseId: courseId || '',
          courseName: courseName || '',
          genreId: genreId,
          genreName: genreName,
          subGenreId: '',
          subGenreName: '',
          menuId: '',
          menuName: '',
        };
        setCopyModalData(copyModalData);
        setShowCopyModal(true);
      } else if (selectedElement.dataset.type === 'subgenre') {
        const genreId = selectedElement.dataset.genreid || '';
        const genreName = selectedElement.dataset.genre_name || '';
        const subgenreId = Number(selectedElement.value);
        const subgenreName = selectedElement.dataset.subgenre_name || '';

        // compose copyModalData
        const copyModalData: CopyModalData = {
          menuBookId: menuBookId || '',
          menuBookName: menuBookName || '',
          courseId: courseId || '',
          courseName: courseName || '',
          genreId: genreId,
          genreName: genreName,
          subGenreId: subgenreId,
          subGenreName: subgenreName,
          menuId: '',
          menuName: '',
        };
        setCopyModalData(copyModalData);
        setShowCopyModal(true);
      } else if (selectedElement.dataset.type === 'menu') {
        const genreId = selectedElement.dataset.genreid || '';
        const genreName = selectedElement.dataset.genre_name || '';
        const subgenreId = selectedElement.dataset.subgenreid || '';
        const subgenreName = selectedElement.dataset.subgenre_name || '';
        const menuId = Number(selectedElement.value);
        const menuName = selectedElement.dataset.menu_name || '';

        // compose copyModalData
        const copyModalData: CopyModalData = {
          menuBookId: menuBookId || '',
          menuBookName: menuBookName || '',
          courseId: courseId || '',
          courseName: courseName || '',
          genreId: genreId,
          genreName: genreName,
          subGenreId: subgenreId,
          subGenreName: subgenreName,
          menuId: menuId,
          menuName: menuName,
        };
        setCopyModalData(copyModalData);
        setShowCopyModal(true);
      }
    }
  }

  function handleCloseCopyModal() {
    handleUnSelectAll();
    setShowCopyModal(false);
    setCopyModalData(undefined);
  }

  function handleSaveCopyModalSuccess(status: boolean) {
    if (status) {
      handleCloseCopyModal();
      loadMenuBookEditorData();

      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('MenuBookListPage.warn_copy_success')} />);
    } else {
      showGlobalModal(() => <AlertModal title={t('App.info')} message={t('MenuBookListPage.warn_copy_failed')} />);
    }
  }

  // check form data changed
  function checkFormDataChanged() {
    return !_.isEqual(rootData.current, data);
  }

  return (
    <Fragment>
      {showGenreAddModal &&
        genreAddModalData &&
        ReactDOM.createPortal(
          <GenreAddModal
            isShowModal={showGenreAddModal}
            exceptGenreIds={genreAddModalData?.exceptGenreIds}
            onReflect={handleReflectGenreAddModal}
            onClose={handleCloseGenreAddModal}
          ></GenreAddModal>,
          document.body
        )}

      {showSubGenreAddModal &&
        subgenreAddModalData &&
        ReactDOM.createPortal(
          <SubGenreAddModal
            isShowModal={showSubGenreAddModal}
            genreId={subgenreAddModalData.genreId}
            genreName={subgenreAddModalData.genreName}
            exceptSubgenreIds={subgenreAddModalData.exceptSubgenreIds}
            onReflect={handleReflectSubGenreAddModal}
            onClose={handleCloseSubGenreAddModal}
          ></SubGenreAddModal>,
          document.body
        )}

      {showMenuAddModal &&
        menuAddModalData &&
        ReactDOM.createPortal(
          <MenuAddModal
            isShowModal={showMenuAddModal}
            genreId={menuAddModalData.genreId}
            subGenreId={menuAddModalData.subGenreId}
            // genreSubgenreId={menuAddModalData.genreSubgenreId}
            genreName={menuAddModalData.genreName}
            subgenreName={menuAddModalData.subgenreName}
            exceptMenuIds={menuAddModalData.exceptMenuIds}
            menuType={MENU_TYPE.MAIN_MENU}
            dataDate={menuAddModalData.dataDate}
            onReflect={handleReflectMenuAddModal}
            onClose={handleCloseMenuAddModal}
          ></MenuAddModal>,
          document.body
        )}

      {showCopyModal &&
        copyModalData &&
        ReactDOM.createPortal(
          <CopyModal
            menuBookId={copyModalData.menuBookId}
            menuBookName={copyModalData.menuBookName}
            courseId={copyModalData.courseId}
            courseName={copyModalData.courseName}
            genreId={copyModalData.genreId}
            genreName={copyModalData.genreName}
            subGenreId={copyModalData.subGenreId}
            subGenreName={copyModalData.subGenreName}
            menuId={copyModalData.menuId}
            menuName={copyModalData.menuName}
            show={showCopyModal}
            onClose={handleCloseCopyModal}
            onSaveSuccess={handleSaveCopyModalSuccess}
          />,
          document.body
        )}

      <Modal
        size="xl"
        dialogClassName="menu-book-editor-modal"
        show
        backdrop="static"
        onHide={handleClose}
        centered
        keyboard={!(selectedItemIds.length > 0)}
      >
        <Modal.Header closeButton className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <FeatureTitleBar title={t('EditMenuBookModal.head_title')} />
        </Modal.Header>
        <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
          <div className="mb-3">
            <strong>{menuBookId && `${t('MenuBookListPage.menu_book_name')}: `}</strong>
            <span>{menuBookId && menuBookName}</span>
            <strong>{courseId && `${t('MenuBookListPage.course_name')}: `}</strong>
            <span>{courseId && courseName}</span>
          </div>

          <form ref={formSearchRef} className="mb-3" onSubmit={handleSubmit}>
            <Accordion defaultActiveKey="0">
              <Accordion.Item eventKey="0">
                <Accordion.Header>{t('MenuBookListPage.search')}</Accordion.Header>
                <Accordion.Body onExited={handleCollapsedFormSearch}>
                  <Row>
                    <fieldset className="col-sm-9 border rounded-3 px-3 pb-3">
                      <legend className="float-none small w-auto px-3">{t('EditMenuBookModal.menu')}</legend>

                      <Row>
                        <Col sm={4}>
                          <Row className="mb-3">
                            <Col sm={4} className="text-end">
                              <Form.Label htmlFor="txt-menu-book-name" className="col-form-label">
                                {t('MenuBookListPage.delete')}：
                              </Form.Label>
                            </Col>
                            <Col sm={8}>
                              {Object.keys(SHOW_STATUS_SOURCE).map(function (key) {
                                return (
                                  <Form.Check
                                    key={`menu-show-delete-${key}`}
                                    className="col-form-label1 form-label1 py-1"
                                    type="radio"
                                    name="show_flg"
                                    id={`menu-show-delete-${key}`}
                                    value={key}
                                    defaultChecked={key === SEARCH_SHOW_DELETE_KEY_DEFAULT}
                                    label={SHOW_STATUS_SOURCE[key]}
                                  />
                                );
                              })}
                            </Col>
                          </Row>
                        </Col>

                        <Col sm={8}>
                          <fieldset className="border rounded-3 px-3">
                            <legend className="float-none small w-auto px-3">
                              {t('EditMenuBookModal.related_pos')}
                            </legend>
                            <Row>
                              <Col sm={3} className="text-end">
                                <Form.Label htmlFor="txt-menu-book-name" className="col-form-label">
                                  {t('EditMenuBookModal.deadline')}：
                                </Form.Label>
                              </Col>
                              <Col sm={9}>
                                <Form.Label className="col-form-label">
                                  {t('EditMenuBookModal.valid_date_title')}
                                </Form.Label>
                                <br />
                                <div style={{ maxWidth: '160px' }}>
                                  <ReactDatePicker
                                    locale="ja"
                                    selected={formSearchValue.dataDate}
                                    onChange={(date: Date) => {
                                      setFormSearchValue({ ...formSearchValue, dataDate: date });
                                    }}
                                    customInput={<CustomInputDatePicker />}
                                    dateFormat="yyyy/MM/dd"
                                    name="data_date"
                                  />
                                </div>
                                <Form.Label className="col-form-label">
                                  {t('EditMenuBookModal.valid_date_note')}
                                </Form.Label>
                              </Col>
                            </Row>
                          </fieldset>
                        </Col>
                      </Row>
                    </fieldset>

                    <Col sm={3} className="text-xl-center text-xxl-start">
                      <Button type="submit">{t('MenuBookListPage.search')}</Button>
                    </Col>
                  </Row>
                </Accordion.Body>
                <div className="accordion-body accordion-summary">
                  <Row>
                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('MenuBookListPage.delete')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {SHOW_STATUS_SOURCE[formSearchValue.showFlg] || '-'}
                      </Form.Label>
                    </Col>

                    <Col sm={'auto'}>
                      <Form.Label className="col-form-label">{t('EditMenuBookModal.deadline')}：</Form.Label>
                      <Form.Label className="col-form-label">
                        {moment(formSearchValue.dataDate).format(DATE_FORMAT_YYYY_MM_DD)}
                      </Form.Label>
                    </Col>
                  </Row>
                </div>
              </Accordion.Item>
            </Accordion>
          </form>

          <SortingGuide />

          <div className="d-flex justify-content-between align-items-center mt-3 mb-2">
            <div>
              <div className="d-inline-block me-4">
                <Button
                  variant="link"
                  className="px-0 text-decoration-none"
                  onClick={handleCollapseAll}
                  style={{ minWidth: 'auto' }}
                >
                  {t('EditMenuBookModal.collapse_all')}
                </Button>
                <span>／</span>
                <Button
                  variant="link"
                  className="px-0 text-decoration-none"
                  onClick={handleExpandAll}
                  style={{ minWidth: 'auto' }}
                >
                  {t('EditMenuBookModal.expand_all')}
                </Button>
              </div>
            </div>
            <div>
              <Button ref={btnAddGenreRef} className="ms-3" onClick={handleOpenGenreAddModal}>
                {t('EditMenuBookModal.add_genre')}
              </Button>
              <Button ref={btnAddSubgenreMenuRef} className="ms-5" onClick={handleOpenAddSubgenreOrAddMenuModal}>
                {t('EditMenuBookModal.add_subgenre_or_add_menu')}
              </Button>
              <Button ref={btnCopyRef} className="ms-3" onClick={handleOpenCopyModal}>
                {t('EditMenuBookModal.copy')}
              </Button>
              <Button ref={btnDeleteRef} className="btn--red ms-3" onClick={handleDelete}>
                {t('EditMenuBookModal.delete')}
              </Button>
              <Button className="btn--dark-green ms-5" onClick={handleSave.bind(null, false)}>
                {t('EditMenuBookModal.save')}
              </Button>
            </div>
          </div>

          <div id="mb-editor-table" className="dan-sticky-table" style={{ maxHeight: '400px' }}>
            <div className="dan-table">
              <div className="dan-thead">
                <div className="dan-tr">
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[0]}%` }}>
                    {t('MenuBookListPage.selection')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[1]}%` }}>
                    {t('EditMenuBookModal.genre')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[2]}%` }}>
                    {t('EditMenuBookModal.subgenre')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[3]}%` }}>
                    {t('EditMenuBookModal.menu')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[4]}%` }}>
                    {t('EditMenuBookModal.sub_menu_group')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[5]}%` }}>
                    {t('EditMenuBookModal.sub_menu')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[6]}%` }}>
                    {t('EditMenuBookModal.default_priority')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[7]}%` }}>
                    {t('EditMenuBookModal.sub_menu_group')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[8]}%` }}>
                    {t('EditMenuBookModal.sub_menu')}
                  </div>
                  <div className="dan-th" style={{ width: `${COLUMN_WIDTH[9]}%` }}>
                    {t('EditMenuBookModal.default_priority')}
                  </div>
                </div>
              </div>
              <DragDropContext onDragStart={onDragStart} onDragEnd={onDragEnd}>
                <div className="position-relative">
                  <GenreRowList menuBookId={menuBookId} onSelectedRowChanged={handleSelectedRowChanged} />
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH[0]}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH[0] + COLUMN_WIDTH[1]}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH.slice(0, 3).reduce((sum, width) => sum + width, 0)}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH.slice(0, 4).reduce((sum, width) => sum + width, 0)}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH.slice(0, 5).reduce((sum, width) => sum + width, 0)}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH.slice(0, 6).reduce((sum, width) => sum + width, 0)}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH.slice(0, 7).reduce((sum, width) => sum + width, 0)}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH.slice(0, 8).reduce((sum, width) => sum + width, 0)}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                  <div
                    style={{
                      position: 'absolute',
                      top: '0',
                      left: `${COLUMN_WIDTH.slice(0, 9).reduce((sum, width) => sum + width, 0)}%`,
                      width: '1px',
                      height: '100%',
                      background: '#ced4da',
                    }}
                  ></div>
                </div>
              </DragDropContext>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </Fragment>
  );
}

export default MenuBookEditorModal;
